import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "auditReportRadio",
    "gapPerTradeUnitRadio",
    "auditReportSection",
    "gapPerTradeUnitSection",
    "auditReportSelectionError",
    "gapPerTradeUnitSelectionError",
    "workerOrganizationsSection",
    "pieceRateWorkersSection",
    "productInfoSection",
    "indirectContractingSection",
    "workerOrganizationsMandatory",
    "pieceRateWorkersMandatory",
    "productInfoMandatory",
    "indirectContractingMandatory",
    // Banana facility mandatory badges
    "workerOrganizationsBananaMandatory",
    "pieceRateWorkersBananaMandatory",
    "productInfoBananaMandatory",
    "indirectContractingBananaMandatory",
    "numberOfTradeUnionsInput",
    "workersRepresentedByOtherOrgRadios",
    "workersRepresentedByOtherOrgContainer",
    "workersCoveredByCbaRadios",
    "workersCoveredByCbaContainer",
    "numberOfPieceRateWorkersInput",
    "totalAnnualProductionInput",
    "unitOfProductionSelect",
    "workersEmployedViaIntermediariesRadios",
    "numberOfThirdPartyWorkersContainer",
    "numberOfThirdPartyWorkersInput",
    "activitiesOutsourcedRadios",
    // Living Wage Contributions targets
    "livingWageContributionsSection",
    "receivesContributionsYesRadio",
    "receivesContributionsNoRadio",
    "contributionsSelectionError",
    "contributionsFieldsError",
    "contributionsSumError",
  ];

  static values = {
    isBananaFacility: Boolean,
  };

  connect() {
    this.initializeIndirectContractingState();
    this.updateMandatoryIndicators();
    this.attachInputListeners();
    this.attachProductListListener();
    this.attachFormSubmitListener();
    this.toggleThirdPartyWorkersField(); // Show field if "Yes" is already selected
    this.toggleOtherOrgQuestion(); // Hide question if unions > 0
  }

  initializeIndirectContractingState() {
    // Track whether the user has explicitly interacted with the indirect contracting radios.
    // Since these fields have database defaults (false), the "No" radio is pre-checked
    // without user interaction. We track explicit interaction for validation.
    this.intermediariesExplicitlySet = false;
    this.outsourcedExplicitlySet = false;

    // Check if "Yes" is selected - that means user explicitly chose Yes (since default is No)
    const intermediariesYesSelected =
      this.workersEmployedViaIntermediariesRadiosTargets.some(
        (radio) => radio.checked && radio.value === "true"
      );
    const outsourcedYesSelected = this.activitiesOutsourcedRadiosTargets.some(
      (radio) => radio.checked && radio.value === "true"
    );

    // If "Yes" is selected, it was explicitly set by the user
    if (intermediariesYesSelected) {
      this.intermediariesExplicitlySet = true;
    }
    if (outsourcedYesSelected) {
      this.outsourcedExplicitlySet = true;
    }

    // Check if the number_of_third_party_workers field has a value - if so, user interacted
    if (
      this.hasNumberOfThirdPartyWorkersInputTarget &&
      this.numberOfThirdPartyWorkersInputTarget.value.trim() !== ""
    ) {
      this.intermediariesExplicitlySet = true;
    }
  }

  // Helper method to check if Audit Report "Yes" is selected
  isAuditReportSelected() {
    return this.auditReportRadioTargets.some(
      (radio) => radio.checked && radio.value === "true"
    );
  }

  // Helper method to check if Gap Per Trade Unit "Yes" is selected
  isGapPerTradeUnitSelected() {
    return this.gapPerTradeUnitRadioTargets.some(
      (radio) => radio.checked && radio.value === "true"
    );
  }

  // Helper method to check if audit-related sections should be required
  // (Audit Report selected OR this is a banana facility)
  isAuditSectionsRequired() {
    return this.isAuditReportSelected() || this.isBananaFacilityValue;
  }

  // Helper method to check if product info section should be required
  // (Gap Per Trade Unit selected OR this is a banana facility)
  isProductInfoRequired() {
    return this.isGapPerTradeUnitSelected() || this.isBananaFacilityValue;
  }

  // Helper method to check if any Audit Report option is selected (Yes or No)
  isAuditReportOptionSelected() {
    return this.auditReportRadioTargets.some((radio) => radio.checked);
  }

  // Helper method to check if any Gap Per Trade Unit option is selected (Yes or No)
  isGapPerTradeUnitOptionSelected() {
    return this.gapPerTradeUnitRadioTargets.some((radio) => radio.checked);
  }

  // Handler for when addon selection changes - clear error styling
  onAddonSelectionChanged() {
    // Clear Audit Report error if a selection is made
    if (
      this.isAuditReportOptionSelected() &&
      this.hasAuditReportSectionTarget
    ) {
      this.auditReportSectionTarget.classList.remove(
        "border-red-300",
        "bg-red-50"
      );
      this.auditReportSectionTarget.classList.add(
        "border-gray-modern-200",
        "bg-white"
      );
      if (this.hasAuditReportSelectionErrorTarget) {
        this.auditReportSelectionErrorTarget.classList.add("hidden");
      }
    }

    // Clear Gap Per Trade Unit error if a selection is made
    if (
      this.isGapPerTradeUnitOptionSelected() &&
      this.hasGapPerTradeUnitSectionTarget
    ) {
      this.gapPerTradeUnitSectionTarget.classList.remove(
        "border-red-300",
        "bg-red-50"
      );
      this.gapPerTradeUnitSectionTarget.classList.add(
        "border-gray-modern-200",
        "bg-white"
      );
      if (this.hasGapPerTradeUnitSelectionErrorTarget) {
        this.gapPerTradeUnitSelectionErrorTarget.classList.add("hidden");
      }
    }
  }

  attachFormSubmitListener() {
    // Form submit is now handled via Stimulus action in the template
    // This method is kept for backwards compatibility but does nothing
  }

  handleFormSubmit(event) {
    // Validate all mandatory sections before allowing submission
    if (!this.validateAllMandatorySections()) {
      event.preventDefault();
      event.stopImmediatePropagation(); // Prevent loading overlay from showing
      this.highlightInvalidSections();
      this.scrollToFirstInvalidSection();
    } else {
      // Clear error styling when validation passes
      this.clearInvalidSectionHighlighting();
    }
  }

  highlightInvalidSections() {
    // Highlight Audit Report selection if not selected (always required)
    if (this.hasAuditReportSectionTarget) {
      if (!this.isAuditReportOptionSelected()) {
        this.auditReportSectionTarget.classList.remove(
          "border-gray-modern-200",
          "bg-white"
        );
        this.auditReportSectionTarget.classList.add(
          "border-red-300",
          "bg-red-50"
        );
        if (this.hasAuditReportSelectionErrorTarget) {
          this.auditReportSelectionErrorTarget.classList.remove("hidden");
        }
      } else {
        this.auditReportSectionTarget.classList.remove(
          "border-red-300",
          "bg-red-50"
        );
        this.auditReportSectionTarget.classList.add(
          "border-gray-modern-200",
          "bg-white"
        );
        if (this.hasAuditReportSelectionErrorTarget) {
          this.auditReportSelectionErrorTarget.classList.add("hidden");
        }
      }
    }

    // Highlight Gap Per Trade Unit selection if not selected (always required)
    if (this.hasGapPerTradeUnitSectionTarget) {
      if (!this.isGapPerTradeUnitOptionSelected()) {
        this.gapPerTradeUnitSectionTarget.classList.remove(
          "border-gray-modern-200",
          "bg-white"
        );
        this.gapPerTradeUnitSectionTarget.classList.add(
          "border-red-300",
          "bg-red-50"
        );
        if (this.hasGapPerTradeUnitSelectionErrorTarget) {
          this.gapPerTradeUnitSelectionErrorTarget.classList.remove("hidden");
        }
      } else {
        this.gapPerTradeUnitSectionTarget.classList.remove(
          "border-red-300",
          "bg-red-50"
        );
        this.gapPerTradeUnitSectionTarget.classList.add(
          "border-gray-modern-200",
          "bg-white"
        );
        if (this.hasGapPerTradeUnitSelectionErrorTarget) {
          this.gapPerTradeUnitSelectionErrorTarget.classList.add("hidden");
        }
      }
    }

    // Highlight Living Wage Contributions section if invalid (always required)
    if (this.hasLivingWageContributionsSectionTarget) {
      const validationResult = this.validateLivingWageContributionsValid();

      if (!validationResult.valid) {
        this.livingWageContributionsSectionTarget.classList.remove(
          "border-gray-modern-200",
          "bg-white"
        );
        this.livingWageContributionsSectionTarget.classList.add(
          "border-red-300",
          "bg-red-50"
        );

        // Hide all error messages first
        if (this.hasContributionsSelectionErrorTarget) {
          this.contributionsSelectionErrorTarget.classList.add("hidden");
        }
        if (this.hasContributionsFieldsErrorTarget) {
          this.contributionsFieldsErrorTarget.classList.add("hidden");
        }
        if (this.hasContributionsSumErrorTarget) {
          this.contributionsSumErrorTarget.classList.add("hidden");
        }

        // Show appropriate error message based on error type
        if (validationResult.errorType === "no_selection") {
          if (this.hasContributionsSelectionErrorTarget) {
            this.contributionsSelectionErrorTarget.classList.remove("hidden");
          }
        } else if (validationResult.errorType === "missing_fields") {
          if (this.hasContributionsFieldsErrorTarget) {
            this.contributionsFieldsErrorTarget.classList.remove("hidden");
          }
          this.highlightInvalidContributionFields();
        } else if (validationResult.errorType === "sum_exceeded") {
          if (this.hasContributionsSumErrorTarget) {
            this.contributionsSumErrorTarget.classList.remove("hidden");
          }
          this.highlightInvalidContributionFields();
        }
      } else {
        this.livingWageContributionsSectionTarget.classList.remove(
          "border-red-300",
          "bg-red-50"
        );
        this.livingWageContributionsSectionTarget.classList.add(
          "border-gray-modern-200",
          "bg-white"
        );
        if (this.hasContributionsSelectionErrorTarget) {
          this.contributionsSelectionErrorTarget.classList.add("hidden");
        }
        if (this.hasContributionsFieldsErrorTarget) {
          this.contributionsFieldsErrorTarget.classList.add("hidden");
        }
        if (this.hasContributionsSumErrorTarget) {
          this.contributionsSumErrorTarget.classList.add("hidden");
        }
        this.clearContributionFieldErrors();
      }
    }

    // Highlight Audit Report sections if invalid (or banana facility)
    if (this.isAuditSectionsRequired()) {
      if (!this.validateWorkerOrganizationsValid()) {
        this.workerOrganizationsSectionTarget.classList.remove(
          "border-gray-modern-200",
          "bg-white"
        );
        this.workerOrganizationsSectionTarget.classList.add(
          "border-red-300",
          "bg-red-50"
        );
      } else {
        this.workerOrganizationsSectionTarget.classList.remove(
          "border-red-300",
          "bg-red-50"
        );
        this.workerOrganizationsSectionTarget.classList.add(
          "border-gray-modern-200",
          "bg-white"
        );
      }

      if (!this.validatePieceRateWorkersValid()) {
        this.pieceRateWorkersSectionTarget.classList.remove(
          "border-gray-modern-200",
          "bg-white"
        );
        this.pieceRateWorkersSectionTarget.classList.add(
          "border-red-300",
          "bg-red-50"
        );
      } else {
        this.pieceRateWorkersSectionTarget.classList.remove(
          "border-red-300",
          "bg-red-50"
        );
        this.pieceRateWorkersSectionTarget.classList.add(
          "border-gray-modern-200",
          "bg-white"
        );
      }

      if (!this.validateIndirectContractingValid()) {
        this.indirectContractingSectionTarget.classList.remove(
          "border-gray-modern-200",
          "bg-white"
        );
        this.indirectContractingSectionTarget.classList.add(
          "border-red-300",
          "bg-red-50"
        );
      } else {
        this.indirectContractingSectionTarget.classList.remove(
          "border-red-300",
          "bg-red-50"
        );
        this.indirectContractingSectionTarget.classList.add(
          "border-gray-modern-200",
          "bg-white"
        );
      }
    }

    // Highlight Gap Per Trade Unit section if invalid (or banana facility)
    if (this.isProductInfoRequired()) {
      if (!this.validateProductInfoValid()) {
        this.productInfoSectionTarget.classList.remove(
          "border-gray-modern-200",
          "bg-white"
        );
        this.productInfoSectionTarget.classList.add(
          "border-red-300",
          "bg-red-50"
        );
      } else {
        this.productInfoSectionTarget.classList.remove(
          "border-red-300",
          "bg-red-50"
        );
        this.productInfoSectionTarget.classList.add(
          "border-gray-modern-200",
          "bg-white"
        );
      }
    }
  }

  clearInvalidSectionHighlighting() {
    // Clear error styling from all sections
    const sections = [
      this.workerOrganizationsSectionTarget,
      this.pieceRateWorkersSectionTarget,
      this.indirectContractingSectionTarget,
      this.productInfoSectionTarget,
    ];

    // Also clear addon sections if they exist
    if (this.hasAuditReportSectionTarget) {
      sections.push(this.auditReportSectionTarget);
      if (this.hasAuditReportSelectionErrorTarget) {
        this.auditReportSelectionErrorTarget.classList.add("hidden");
      }
    }

    if (this.hasGapPerTradeUnitSectionTarget) {
      sections.push(this.gapPerTradeUnitSectionTarget);
      if (this.hasGapPerTradeUnitSelectionErrorTarget) {
        this.gapPerTradeUnitSelectionErrorTarget.classList.add("hidden");
      }
    }

    // Also clear contributions section if it exists
    if (this.hasLivingWageContributionsSectionTarget) {
      sections.push(this.livingWageContributionsSectionTarget);
      if (this.hasContributionsErrorTarget) {
        this.contributionsErrorTarget.classList.add("hidden");
      }
    }

    sections.forEach((section) => {
      section.classList.remove("border-red-300", "bg-red-50");
      section.classList.add("border-gray-modern-200", "bg-white");
    });
  }

  validateAllMandatorySections() {
    let isValid = true;

    // Addon selections are always required
    if (!this.isAuditReportOptionSelected()) {
      isValid = false;
    }
    if (!this.isGapPerTradeUnitOptionSelected()) {
      isValid = false;
    }

    // Living Wage Contributions is always required
    const contributionsValidation = this.validateLivingWageContributionsValid();
    if (!contributionsValidation.valid) {
      isValid = false;
    }

    // Check Audit Report requirements if selected OR if banana facility
    if (this.isAuditSectionsRequired()) {
      if (!this.validateWorkerOrganizationsValid()) {
        isValid = false;
      }
      if (!this.validatePieceRateWorkersValid()) {
        isValid = false;
      }
      if (!this.validateIndirectContractingValid()) {
        isValid = false;
      }
    }

    // Check Gap Per Trade Unit requirements if selected OR if banana facility
    if (this.isProductInfoRequired()) {
      if (!this.validateProductInfoValid()) {
        isValid = false;
      }
    }

    return isValid;
  }

  // Validate that user has selected Yes or No for living wage contributions
  // If Yes is selected, also validate that all contribution fields are filled
  validateLivingWageContributionsValid() {
    if (
      !this.hasReceivesContributionsYesRadioTarget ||
      !this.hasReceivesContributionsNoRadioTarget
    ) {
      return { valid: true, errorType: "none" };
    }

    const yesSelected = this.receivesContributionsYesRadioTarget.checked;
    const noSelected = this.receivesContributionsNoRadioTarget.checked;

    // Must select Yes or No
    if (!yesSelected && !noSelected) {
      return { valid: false, errorType: "no_selection" };
    }

    // If No is selected, no further validation needed
    if (noSelected) {
      return { valid: true, errorType: "none" };
    }

    // If Yes is selected, validate all contribution entries have required fields filled
    return this.validateContributionFields();
  }

  // Validate all contribution entries have required fields filled
  validateContributionFields() {
    const contributionEntries = document.querySelectorAll(
      "[data-contribution-entry]"
    );

    // If Yes is selected but no contributions exist, that's invalid
    if (contributionEntries.length === 0) {
      return { valid: false, errorType: "missing_fields" };
    }

    let hasMissingFields = false;
    let hasSumExceeded = false;

    contributionEntries.forEach((entry) => {
      // Provider name is required
      const providerInput = entry.querySelector(
        "[data-contribution-field='provider_name']"
      );
      if (!providerInput || providerInput.value.trim() === "") {
        hasMissingFields = true;
      }

      // Contribution type is required
      const typeSelect = entry.querySelector(
        "[data-contribution-field='contribution_type']"
      );
      if (!typeSelect || typeSelect.value === "") {
        hasMissingFields = true;
      }

      // Total amount is required
      const totalAmountInput = entry.querySelector(
        "[data-contribution-field='total_amount_received']"
      );
      if (
        !totalAmountInput ||
        totalAmountInput.value.trim() === "" ||
        parseFloat(totalAmountInput.value) < 0
      ) {
        hasMissingFields = true;
      }

      // Amount paid in cash is required
      const cashInput = entry.querySelector(
        "[data-contribution-field='amount_paid_to_workers']"
      );
      if (
        !cashInput ||
        cashInput.value.trim() === "" ||
        parseFloat(cashInput.value) < 0
      ) {
        hasMissingFields = true;
      }

      // Amount paid in vouchers is required
      const vouchersInput = entry.querySelector(
        "[data-contribution-field='annual_usage_amount']"
      );
      if (
        !vouchersInput ||
        vouchersInput.value.trim() === "" ||
        parseFloat(vouchersInput.value) < 0
      ) {
        hasMissingFields = true;
      }

      // Cash + vouchers should not exceed total amount
      if (totalAmountInput && cashInput && vouchersInput) {
        const total = parseFloat(totalAmountInput.value) || 0;
        const cash = parseFloat(cashInput.value) || 0;
        const vouchers = parseFloat(vouchersInput.value) || 0;
        if (cash + vouchers > total) {
          hasSumExceeded = true;
        }
      }
    });

    if (hasMissingFields) {
      return { valid: false, errorType: "missing_fields" };
    }
    if (hasSumExceeded) {
      return { valid: false, errorType: "sum_exceeded" };
    }
    return { valid: true, errorType: "none" };
  }

  // Called when user selects Yes or No for contributions
  onContributionsSelectionChanged() {
    // Clear error highlighting when user makes a selection
    if (this.hasLivingWageContributionsSectionTarget) {
      this.livingWageContributionsSectionTarget.classList.remove(
        "border-red-300",
        "bg-red-50"
      );
      this.livingWageContributionsSectionTarget.classList.add(
        "border-gray-modern-200",
        "bg-white"
      );
    }
    if (this.hasContributionsSelectionErrorTarget) {
      this.contributionsSelectionErrorTarget.classList.add("hidden");
    }
    if (this.hasContributionsFieldsErrorTarget) {
      this.contributionsFieldsErrorTarget.classList.add("hidden");
    }
    if (this.hasContributionsSumErrorTarget) {
      this.contributionsSumErrorTarget.classList.add("hidden");
    }
    this.clearContributionFieldErrors();
  }

  // Highlight invalid contribution fields using existing error pattern (border-red-500 bg-red-50)
  highlightInvalidContributionFields() {
    const contributionEntries = document.querySelectorAll(
      "[data-contribution-entry]"
    );

    contributionEntries.forEach((entry) => {
      // Provider name validation
      const providerInput = entry.querySelector(
        "[data-contribution-field='provider_name']"
      );
      const providerError = entry.querySelector(".provider_name-error");
      if (providerInput && providerInput.value.trim() === "") {
        providerInput.classList.add("border-red-500", "bg-red-50");
        if (providerError) {
          providerError.textContent = "Provider name is required";
          providerError.classList.remove("hidden");
        }
      }

      // Contribution type validation
      const typeSelect = entry.querySelector(
        "[data-contribution-field='contribution_type']"
      );
      const typeError = entry.querySelector(".contribution_type-error");
      if (typeSelect && typeSelect.value === "") {
        typeSelect.classList.add("border-red-500", "bg-red-50");
        if (typeError) {
          typeError.textContent = "Contribution type is required";
          typeError.classList.remove("hidden");
        }
      }

      // Total amount validation
      const totalAmountInput = entry.querySelector(
        "[data-contribution-field='total_amount_received']"
      );
      const totalAmountError = entry.querySelector(
        ".total_amount_received-error"
      );
      if (
        totalAmountInput &&
        (totalAmountInput.value.trim() === "" ||
          parseFloat(totalAmountInput.value) < 0)
      ) {
        totalAmountInput.classList.add("border-red-500", "bg-red-50");
        if (totalAmountError) {
          totalAmountError.textContent = "Total amount is required";
          totalAmountError.classList.remove("hidden");
        }
      }

      // Amount paid in cash validation
      const cashInput = entry.querySelector(
        "[data-contribution-field='amount_paid_to_workers']"
      );
      const cashError = entry.querySelector(".amount_paid_to_workers-error");
      if (
        cashInput &&
        (cashInput.value.trim() === "" || parseFloat(cashInput.value) < 0)
      ) {
        cashInput.classList.add("border-red-500", "bg-red-50");
        if (cashError) {
          cashError.textContent = "Amount paid in cash is required";
          cashError.classList.remove("hidden");
        }
      }

      // Amount paid in vouchers validation
      const vouchersInput = entry.querySelector(
        "[data-contribution-field='annual_usage_amount']"
      );
      const vouchersError = entry.querySelector(".annual_usage_amount-error");
      if (
        vouchersInput &&
        (vouchersInput.value.trim() === "" ||
          parseFloat(vouchersInput.value) < 0)
      ) {
        vouchersInput.classList.add("border-red-500", "bg-red-50");
        if (vouchersError) {
          vouchersError.textContent = "Amount paid in vouchers is required";
          vouchersError.classList.remove("hidden");
        }
      }

      // Validate cash + vouchers does not exceed total
      if (totalAmountInput && cashInput && vouchersInput) {
        const total = parseFloat(totalAmountInput.value) || 0;
        const cash = parseFloat(cashInput.value) || 0;
        const vouchers = parseFloat(vouchersInput.value) || 0;

        if (cash + vouchers > total) {
          // Highlight both cash and vouchers fields
          cashInput.classList.add("border-red-500", "bg-red-50");
          vouchersInput.classList.add("border-red-500", "bg-red-50");

          // Show error on cash field (or both)
          if (cashError && cashError.classList.contains("hidden")) {
            cashError.textContent =
              "Cash + vouchers cannot exceed total amount";
            cashError.classList.remove("hidden");
          }
          if (vouchersError && vouchersError.classList.contains("hidden")) {
            vouchersError.textContent =
              "Cash + vouchers cannot exceed total amount";
            vouchersError.classList.remove("hidden");
          }
        }
      }
    });
  }

  // Clear all contribution field errors using existing pattern
  clearContributionFieldErrors() {
    const contributionEntries = document.querySelectorAll(
      "[data-contribution-entry]"
    );

    contributionEntries.forEach((entry) => {
      // Clear all error messages
      entry.querySelectorAll("[class*='-error']").forEach((errorElement) => {
        errorElement.classList.add("hidden");
        errorElement.textContent = "";
      });

      // Remove error styling from all input fields
      entry.querySelectorAll("input, select, textarea").forEach((field) => {
        field.classList.remove("border-red-500", "bg-red-50");
      });
    });
  }

  validateIndirectContractingValid() {
    // Section must be explicitly reviewed by the user (opened)
    if (!this.intermediariesExplicitlySet || !this.outsourcedExplicitlySet) {
      return false;
    }

    // If "Yes" is selected for intermediaries, number of workers is required
    const intermediariesYesSelected =
      this.workersEmployedViaIntermediariesRadiosTargets.some(
        (radio) => radio.checked && radio.value === "true"
      );

    if (intermediariesYesSelected) {
      const hasNumberOfWorkers =
        this.hasNumberOfThirdPartyWorkersInputTarget &&
        this.numberOfThirdPartyWorkersInputTarget.value.trim() !== "" &&
        parseInt(this.numberOfThirdPartyWorkersInputTarget.value) >= 0;

      if (!hasNumberOfWorkers) {
        return false;
      }
    }

    return true;
  }

  validateWorkerOrganizationsValid() {
    const hasTradeUnions =
      this.numberOfTradeUnionsInputTarget.value.trim() !== "" &&
      parseInt(this.numberOfTradeUnionsInputTarget.value) >= 0;

    const numberOfUnions =
      parseInt(this.numberOfTradeUnionsInputTarget.value) || 0;

    // For banana facilities, CBA answer is required
    const hasCbaAnswer = this.hasWorkersCoveredByCbaRadiosTarget
      ? this.workersCoveredByCbaRadiosTargets.some((radio) => radio.checked)
      : true;

    // If number of unions > 0, the representation answer is not required
    if (numberOfUnions > 0) {
      // For banana facilities, also require CBA answer
      if (this.isBananaFacilityValue) {
        return hasTradeUnions && hasCbaAnswer;
      }
      return hasTradeUnions;
    }

    const hasRepresentationAnswer =
      this.workersRepresentedByOtherOrgRadiosTargets.some(
        (radio) => radio.checked
      );

    // For banana facilities, also require CBA answer
    if (this.isBananaFacilityValue) {
      return hasTradeUnions && hasRepresentationAnswer && hasCbaAnswer;
    }

    return hasTradeUnions && hasRepresentationAnswer;
  }

  validatePieceRateWorkersValid() {
    const hasPieceRateWorkers =
      this.numberOfPieceRateWorkersInputTarget.value.trim() !== "" &&
      parseInt(this.numberOfPieceRateWorkersInputTarget.value) >= 0;

    return hasPieceRateWorkers;
  }

  validateProductInfoValid() {
    const hasProduction =
      this.totalAnnualProductionInputTarget.value.trim() !== "" &&
      parseInt(this.totalAnnualProductionInputTarget.value) > 0;

    const hasUnit = this.unitOfProductionSelectTarget.value.trim() !== "";

    const productsList = document.querySelector(
      "[data-calculation-wizard-product-info-target='productsList']"
    );
    const hasProducts =
      productsList &&
      productsList.querySelectorAll("[data-product-id]").length > 0;

    return hasProduction && hasUnit && hasProducts;
  }

  scrollToFirstInvalidSection() {
    // Scroll to the first invalid section
    // Addon selections are checked first (always required)
    if (
      this.hasAuditReportSectionTarget &&
      !this.isAuditReportOptionSelected()
    ) {
      this.auditReportSectionTarget.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    } else if (
      this.hasGapPerTradeUnitSectionTarget &&
      !this.isGapPerTradeUnitOptionSelected()
    ) {
      this.gapPerTradeUnitSectionTarget.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    } else if (
      this.hasLivingWageContributionsSectionTarget &&
      !this.validateLivingWageContributionsValid().valid
    ) {
      this.livingWageContributionsSectionTarget.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    } else if (
      this.isAuditSectionsRequired() &&
      !this.validateWorkerOrganizationsValid()
    ) {
      this.workerOrganizationsSectionTarget.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    } else if (
      this.isAuditSectionsRequired() &&
      !this.validatePieceRateWorkersValid()
    ) {
      this.pieceRateWorkersSectionTarget.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    } else if (
      this.isAuditSectionsRequired() &&
      !this.validateIndirectContractingValid()
    ) {
      this.indirectContractingSectionTarget.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    } else if (
      this.isProductInfoRequired() &&
      !this.validateProductInfoValid()
    ) {
      this.productInfoSectionTarget.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }

  attachProductListListener() {
    // Listen for product list changes
    document.addEventListener("product-list-changed", () => {
      this.validateProductInfo();
    });
  }

  toggleAuditReport() {
    this.updateMandatoryIndicators();
  }

  toggleGapPerTradeUnit() {
    this.updateMandatoryIndicators();
  }

  attachInputListeners() {
    // Listen to changes on Worker Organizations inputs
    if (this.hasNumberOfTradeUnionsInputTarget) {
      this.numberOfTradeUnionsInputTarget.addEventListener("input", () => {
        this.toggleOtherOrgQuestion();
        this.validateWorkerOrganizations();
      });
    }

    if (this.hasWorkersRepresentedByOtherOrgRadiosTarget) {
      this.workersRepresentedByOtherOrgRadiosTargets.forEach((radio) => {
        radio.addEventListener("change", () =>
          this.validateWorkerOrganizations()
        );
      });
    }

    // Listen to changes on Piece Rate Workers input
    if (this.hasNumberOfPieceRateWorkersInputTarget) {
      this.numberOfPieceRateWorkersInputTarget.addEventListener("input", () =>
        this.validatePieceRateWorkers()
      );
    }

    // Listen to changes on Product Info inputs
    if (this.hasTotalAnnualProductionInputTarget) {
      this.totalAnnualProductionInputTarget.addEventListener("input", () =>
        this.validateProductInfo()
      );
    }

    if (this.hasUnitOfProductionSelectTarget) {
      this.unitOfProductionSelectTarget.addEventListener("change", () =>
        this.validateProductInfo()
      );
    }

    // Listen to changes on Indirect Contracting radios
    if (this.hasWorkersEmployedViaIntermediariesRadiosTarget) {
      this.workersEmployedViaIntermediariesRadiosTargets.forEach((radio) => {
        radio.addEventListener("click", () => {
          this.toggleThirdPartyWorkersField();
          this.validateIndirectContracting();
        });
      });
    }

    // Listen to changes on number of third party workers input
    if (this.hasNumberOfThirdPartyWorkersInputTarget) {
      this.numberOfThirdPartyWorkersInputTarget.addEventListener("input", () =>
        this.validateIndirectContracting()
      );
    }

    // Listen for clicks on the indirect contracting section header to mark as reviewed
    if (this.hasIndirectContractingSectionTarget) {
      const sectionButton =
        this.indirectContractingSectionTarget.querySelector("button");
      if (sectionButton) {
        sectionButton.addEventListener("click", () => {
          this.intermediariesExplicitlySet = true;
          this.outsourcedExplicitlySet = true;
          this.validateIndirectContracting();
        });
      }
    }
  }

  updateMandatoryIndicators() {
    const auditReportSelected = this.isAuditReportSelected();
    const gapPerTradeUnitSelected = this.isGapPerTradeUnitSelected();
    const isBananaFacility = this.isBananaFacilityValue;

    // Show/hide mandatory indicators based on audit report selection
    if (auditReportSelected) {
      this.workerOrganizationsMandatoryTarget.classList.remove("hidden");
      this.pieceRateWorkersMandatoryTarget.classList.remove("hidden");
      this.indirectContractingMandatoryTarget.classList.remove("hidden");
    } else {
      this.workerOrganizationsMandatoryTarget.classList.add("hidden");
      this.pieceRateWorkersMandatoryTarget.classList.add("hidden");
      this.indirectContractingMandatoryTarget.classList.add("hidden");
    }

    // Show/hide mandatory indicator for Product Info based on gap per trade unit selection
    if (gapPerTradeUnitSelected) {
      this.productInfoMandatoryTarget.classList.remove("hidden");
    } else {
      this.productInfoMandatoryTarget.classList.add("hidden");
    }

    // For banana facilities, show banana-specific mandatory badges
    // These badges are shown in addition to (or instead of) the audit/gap badges
    if (isBananaFacility) {
      if (this.hasWorkerOrganizationsBananaMandatoryTarget) {
        this.workerOrganizationsBananaMandatoryTarget.classList.remove("hidden");
      }
      if (this.hasPieceRateWorkersBananaMandatoryTarget) {
        this.pieceRateWorkersBananaMandatoryTarget.classList.remove("hidden");
      }
      if (this.hasIndirectContractingBananaMandatoryTarget) {
        this.indirectContractingBananaMandatoryTarget.classList.remove("hidden");
      }
      if (this.hasProductInfoBananaMandatoryTarget) {
        this.productInfoBananaMandatoryTarget.classList.remove("hidden");
      }
    } else {
      if (this.hasWorkerOrganizationsBananaMandatoryTarget) {
        this.workerOrganizationsBananaMandatoryTarget.classList.add("hidden");
      }
      if (this.hasPieceRateWorkersBananaMandatoryTarget) {
        this.pieceRateWorkersBananaMandatoryTarget.classList.add("hidden");
      }
      if (this.hasIndirectContractingBananaMandatoryTarget) {
        this.indirectContractingBananaMandatoryTarget.classList.add("hidden");
      }
      if (this.hasProductInfoBananaMandatoryTarget) {
        this.productInfoBananaMandatoryTarget.classList.add("hidden");
      }
    }
  }

  validateWorkerOrganizations() {
    if (!this.isAuditSectionsRequired()) return;

    const isValid = this.validateWorkerOrganizationsValid();

    this.updateSectionValidation(
      this.workerOrganizationsSectionTarget,
      isValid
    );
  }

  validatePieceRateWorkers() {
    if (!this.isAuditSectionsRequired()) return;

    const hasPieceRateWorkers =
      this.numberOfPieceRateWorkersInputTarget.value.trim() !== "" &&
      parseInt(this.numberOfPieceRateWorkersInputTarget.value) >= 0;

    this.updateSectionValidation(
      this.pieceRateWorkersSectionTarget,
      hasPieceRateWorkers
    );
  }

  validateIndirectContracting() {
    if (!this.isAuditSectionsRequired()) return;

    const isValid = this.validateIndirectContractingValid();

    this.updateSectionValidation(
      this.indirectContractingSectionTarget,
      isValid
    );
  }

  validateProductInfo() {
    if (!this.isProductInfoRequired()) return;

    const hasProduction =
      this.totalAnnualProductionInputTarget.value.trim() !== "" &&
      parseInt(this.totalAnnualProductionInputTarget.value) > 0;

    const hasUnit = this.unitOfProductionSelectTarget.value.trim() !== "";

    // Check if there are any products in the list
    const productsList = document.querySelector(
      "[data-calculation-wizard-product-info-target='productsList']"
    );
    const hasProducts =
      productsList &&
      productsList.querySelectorAll("[data-product-id]").length > 0;

    this.updateSectionValidation(
      this.productInfoSectionTarget,
      hasProduction && hasUnit && hasProducts
    );
  }

  updateSectionValidation(section, isValid) {
    if (isValid) {
      section.classList.remove("border-red-300", "bg-red-50");
      section.classList.add("border-gray-modern-200", "bg-white");
    } else {
      section.classList.remove("border-gray-modern-200", "bg-white");
      section.classList.add("border-red-300", "bg-red-50");
    }
  }

  toggleThirdPartyWorkersField() {
    const isYesSelected =
      this.workersEmployedViaIntermediariesRadiosTargets.some(
        (radio) => radio.checked && radio.value === "true"
      );

    if (isYesSelected) {
      this.numberOfThirdPartyWorkersContainerTarget.classList.remove("hidden");
      this.numberOfThirdPartyWorkersContainerTarget.classList.add("sm:flex");
    } else {
      this.numberOfThirdPartyWorkersContainerTarget.classList.add("hidden");
      this.numberOfThirdPartyWorkersContainerTarget.classList.remove("sm:flex");
      // Clear the input value when hiding
      if (this.hasNumberOfThirdPartyWorkersInputTarget) {
        this.numberOfThirdPartyWorkersInputTarget.value = "";
      }
    }
  }

  toggleOtherOrgQuestion() {
    if (!this.hasWorkersRepresentedByOtherOrgContainerTarget) return;

    const numberOfUnions =
      parseInt(this.numberOfTradeUnionsInputTarget.value) || 0;

    if (numberOfUnions > 0) {
      this.workersRepresentedByOtherOrgContainerTarget.classList.add("hidden");
      // Clear the radios when hiding
      this.workersRepresentedByOtherOrgRadiosTargets.forEach((radio) => {
        radio.checked = false;
      });
    } else {
      this.workersRepresentedByOtherOrgContainerTarget.classList.remove(
        "hidden"
      );
    }
  }
}
